import { ApolloError } from 'apollo-boost';
import c from 'classnames';
import ErrorsShower from 'core/components/common/ErrorsShower';
import { container } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { isFilledArray, isMobile } from 'core/utils';
import { getRichContent, isHtmlEmpty } from 'core/utils/html';
import {
    filterCertificates,
    filterIsNonSingle,
    filterMaterials,
    filterRecords,
    filterVideoCourses,
    nearestOffersAndStarted,
    nearestSeminarsAndStarted,
    nearestWebinarsAndStarted,
} from 'core/utils/product-forms/filterProductForms';
import { orderByPrice } from 'core/utils/product-forms/orderProductForms';
import React from 'react';
import FeedbackButton from 'site/components/feedback-product/FeedbackButton';
import Feedbacks from 'site/components/feedback-product/Feedbacks';
import SocialShare from 'site/components/social-share/SocialShare';
import { GetProductDetailDataQuery } from 'site/graphql';
import ProductDescription from './ProductDescription';
import ProductFaqSection from './ProductFaqSection';
import ProductHeaderSection from './ProductHeaderSection';
import ProductPurchaseSection from './ProductPurchaseSection';
import ProductSalesPoints from './ProductSalesPoints';
import ProductSuggestedProductForm from './ProductSuggestedProductForm';
import ProductTeachers from './ProductTeachers';

const styles = (theme: ITheme) =>
    createStyles({
        container: {
            ...container(theme),
        },
        header: {
            backgroundColor: theme.colors.contrastBg,
        },
        content: {
            display: 'flex',
            paddingTop: 1.5 + 'rem',
            margin: '0 -15px',
            [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
                margin: 0,
            },
        },
        footerDesktop: {
            display: 'none',

            [theme.breakpoints.up('md')]: {
                backgroundColor: '#e9e8e8',
                paddingBottom: 1,
                marginBottom: -1,
                display: 'block',
            },
        },
        footerMobile: {
            display: 'block',
            '& + &': {
                marginTop: '1rem',
            },
            [theme.breakpoints.up('md')]: {
                display: 'none',
            },
        },
        footerMobileBg: {
            backgroundColor: '#e9e8e8',
        },
        leftContentColumn: {
            width: 'calc(2/3*100%)',
            padding: '0 15px',
            boxSizing: 'border-box',

            [theme.breakpoints.down('sm')]: {
                width: 100 + '%',
                order: 2,
                marginTop: 1 + 'rem',
                padding: 0,
            },
        },
        rightContentColumn: {
            width: 'calc(1/3*100%)',
            padding: '0 15px',
            boxSizing: 'border-box',
            marginBottom: '1rem',

            [theme.breakpoints.down('sm')]: {
                width: 100 + '%',
                order: 1,
                padding: 0,
                marginBottom: 0,
            },
        },
        feedbackButton: {
            marginLeft: 'auto',
            marginRight: 'auto',
            marginBottom: 2 + 'rem',
            width: 30 + '%',

            [theme.breakpoints.down('sm')]: {
                width: 70 + '%',
            },
        },
    });

export type IProductDetailProps = {
    product: GetProductDetailDataQuery['product'];
    error: ApolloError;
    loading: boolean;
    breadcrumbsContrast?: boolean;
    productId: number;
};

type IProductDetailState = {
    selectedProductFormIndex: number;
};

interface IProductDetailHocOptions {
    ContactsPreview?: React.ComponentType;
}

const mobileSize = 960;

const productDetailHoc = ({ ContactsPreview }: IProductDetailHocOptions) => {
    class ProductDetail extends React.PureComponent<
        IProductDetailProps & WithStyles<typeof styles>,
        IProductDetailState
    > {
        state: IProductDetailState = {
            selectedProductFormIndex: 0,
        };

        handleProductFormSelect = (index: number) => this.setState({ selectedProductFormIndex: index });

        render() {
            const { classes, product, loading, breadcrumbsContrast = true, error, productId } = this.props;
            const { selectedProductFormIndex } = this.state;
            const { productForms = [] } = product;
            const formTypes = productForms.map(_ => _.type);
            const richDescription = getRichContent(product.description);
            const richSalesPoints = getRichContent(product.salesPoints);
            const richWarnings = getRichContent(product.warnings);

            const availableProductForms = filterIsNonSingle([
                ...orderByPrice(nearestOffersAndStarted(productForms)),
                ...nearestSeminarsAndStarted(productForms),
                ...nearestWebinarsAndStarted(productForms),
                ...orderByPrice([
                    ...filterVideoCourses(productForms),
                    ...filterRecords(productForms),
                    ...filterMaterials(productForms),
                    ...filterCertificates(productForms),
                ]),
            ]);

            const selectedProductForm = availableProductForms[selectedProductFormIndex];

            return (
                <ErrorsShower loading={loading} error={error}>
                    <div>
                        <div className={classes.header}>
                            <div className={classes.container}>
                                <ProductHeaderSection
                                    breadcrumbsContrast={breadcrumbsContrast}
                                    product={product}
                                    selectedProductForm={selectedProductForm}
                                />
                            </div>
                        </div>

                        <div className={classes.container}>
                            <div className={classes.content}>
                                <div className={classes.leftContentColumn}>
                                    {!isHtmlEmpty(richSalesPoints) && (
                                        <ProductSalesPoints richSalesPoints={richSalesPoints} />
                                    )}
                                    {!isHtmlEmpty(richDescription) && (
                                        <ProductDescription
                                            product={product}
                                            availableProductForms={availableProductForms}
                                            richDescription={richDescription}
                                            richWarnings={richWarnings}
                                            selectedProductFormIndex={selectedProductFormIndex}
                                        />
                                    )}
                                    {isMobile(mobileSize) && (
                                        <ProductSuggestedProductForm selectedProductForm={selectedProductForm} />
                                    )}
                                    {isFilledArray(product.lecturers) && (
                                        <ProductTeachers lecturers={product.lecturers} />
                                    )}
                                    <Feedbacks />
                                    <div className={classes.feedbackButton}>
                                        <FeedbackButton productId={productId} />
                                    </div>
                                </div>
                                <div className={classes.rightContentColumn}>
                                    <ProductPurchaseSection
                                        onProductFormSelect={this.handleProductFormSelect}
                                        availableProductForms={availableProductForms}
                                        selectedProductFormIndex={selectedProductFormIndex}
                                    />
                                    {!isMobile(mobileSize) && (
                                        <ProductSuggestedProductForm selectedProductForm={selectedProductForm} />
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className={classes.footerDesktop}>
                            <div className={classes.container}>
                                <ProductFaqSection formTypes={formTypes} ContactsPreview={ContactsPreview} />
                                <SocialShare />
                            </div>
                        </div>

                        <div className={c(classes.footerMobile, classes.footerMobileBg)}>
                            <div className={classes.container}>
                                <ProductFaqSection formTypes={formTypes} ContactsPreview={ContactsPreview} />
                            </div>
                        </div>
                        <div className={classes.footerMobile}>
                            <div className={classes.container}>
                                <ProductPurchaseSection
                                    onProductFormSelect={this.handleProductFormSelect}
                                    availableProductForms={availableProductForms}
                                    selectedProductFormIndex={selectedProductFormIndex}
                                    buttonMode
                                />
                                <SocialShare />
                            </div>
                        </div>
                    </div>
                </ErrorsShower>
            );
        }
    }

    return injectSheet(styles, 'ProductDetail')(ProductDetail);
};

export default productDetailHoc;
